import React, { useState, useRef, useEffect } from 'react';
import {
  Monitor,
  Tablet,
  Smartphone,
  Rocket,
  Eye,
  ExternalLink,
  ChevronDown,
  Plus,
  Loader2,
  Sparkles,
  ArrowLeft,
  CheckCircle2,
  Undo2,
  Redo2,
  Layers,
  SlidersHorizontal,
  Download,
  Clock,
} from 'lucide-react';
import { useSite, DeviceMode } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { PRESET_VIEWPORTS } from '../../utils/responsive';
import { isSiteDomainConnected } from '../../utils/security';
import { ResponsiveQAModal } from './ResponsiveQAModal';
import { WebsiteExportModal } from '../export/WebsiteExportModal';

interface EditorHeaderProps {
  onOpenNewPageModal: () => void;
  onOpenLiveSite: () => void;
  onOpenPageSheet?: () => void;
  onBackToDashboard?: () => void;
  onOpenAddSectionModal?: () => void;
  onToggleProperties?: () => void;
  isPropertiesOpen?: boolean;
}

export const EditorHeader: React.FC<EditorHeaderProps> = ({
  onOpenNewPageModal,
  onOpenLiveSite,
  onBackToDashboard,
  onOpenAddSectionModal,
  onToggleProperties,
  isPropertiesOpen,
}) => {
  const {
    activeSite,
    activePageId,
    setActivePageId,
    deviceMode,
    setDeviceMode,
    testViewportWidth,
    setTestViewportWidth,
    isPreviewMode,
    setIsPreviewMode,
    hasUnsavedChanges,
    autosaveStatus,
    publishCurrentSite,
    isPublishing,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useSite();
  const { isSuperAdmin } = useAuth();

  const [isQAModalOpen, setIsQAModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isViewportDropdownOpen, setIsViewportDropdownOpen] = useState(false);
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [customWidthInput, setCustomWidthInput] = useState('');
  const [isCustomWidthActive, setIsCustomWidthActive] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const pageDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsViewportDropdownOpen(false);
      }
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(e.target as Node)) {
        setIsPageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!activeSite) return null;

  const activePage = activeSite.pages?.find((p) => p && p.id === activePageId) || activeSite.pages?.[0];

  const currentPreset = PRESET_VIEWPORTS.find((p) => p.width === testViewportWidth) || {
    label: `${testViewportWidth}px`,
    width: testViewportWidth,
    device: deviceMode,
  };

  const handleCustomWidthSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = parseInt(customWidthInput, 10);
    if (!isNaN(val) && val >= 320 && val <= 2560) {
      setTestViewportWidth(val);
      setIsViewportDropdownOpen(false);
      setIsCustomWidthActive(false);
    }
  };

  return (
    <>
      <header className="flex flex-col bg-[#0B1324] border-b border-[#263750] text-[#F8FAFC] select-none z-30 shrink-0 w-full font-sans shadow-md">
        {/* ==================================================================== */}
        {/* 1. TOP HEADER (approx 72px) */}
        {/* ==================================================================== */}
        <div className="h-[72px] px-5 flex items-center justify-between border-b border-[#263750] gap-4">
          {/* Left: [← Panel] then [✦ Adiosoft PANEL] */}
          <div className="flex items-center gap-3 shrink-0">
            {onBackToDashboard && (
              <button
                type="button"
                onClick={onBackToDashboard}
                className="px-3.5 py-2 rounded-xl bg-[#162238] hover:bg-[#1D2B43] text-[#F8FAFC] hover:text-white transition flex items-center gap-2 text-xs font-semibold border border-[#263750] shadow-sm active:scale-95"
                title="Yönetim Paneline Dön"
              >
                <ArrowLeft className="w-4 h-4 text-[#94A3B8]" />
                <span>Panel</span>
              </button>
            )}

            <div className="flex items-center gap-2.5 pl-1 border-l border-[#263750]/60">
              <div className="w-8 h-8 rounded-xl bg-[#2563EB] flex items-center justify-center text-white font-black text-sm shadow-md shadow-blue-500/20">
                ✦
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span className="font-extrabold text-sm tracking-tight text-[#F8FAFC] leading-none">
                    Adiosoft
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#162238] border border-[#263750] text-[#94A3B8]">
                    PANEL
                  </span>
                </div>
                <span className="text-[11px] text-[#94A3B8] font-medium leading-tight mt-0.5 truncate max-w-[150px] sm:max-w-[200px]">
                  {activeSite.name || 'Web Projesi'}
                </span>
              </div>
            </div>
          </div>

          {/* Center: [Görsel Editör] Active Tab Badge (Primary Blue, subtle shadow) */}
          <div className="hidden md:flex items-center justify-center">
            <div className="px-4 py-1.5 rounded-xl bg-[#2563EB] text-white text-xs font-bold tracking-wide flex items-center gap-2 shadow-lg shadow-blue-600/30 border border-blue-400/30">
              <Sparkles className="w-3.5 h-3.5 text-blue-100" />
              <span>Görsel Editör</span>
            </div>
          </div>

          {/* Right: Actions (Responsive QA, Preview, Save Status, Publish, Live Link) */}
          <div className="flex items-center gap-2.5 shrink-0">
            {/* Responsive QA */}
            <button
              type="button"
              onClick={() => setIsQAModalOpen(true)}
              className="hidden lg:flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#162238] hover:bg-[#1D2B43] text-[#F8FAFC] hover:text-white border border-[#263750] text-xs font-semibold transition"
              title="Responsive Kontrolü ve Optimizasyon"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>Responsive QA</span>
            </button>

            {/* Preview Toggle */}
            <button
              type="button"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-semibold transition ${
                isPreviewMode
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-inner'
                  : 'bg-[#162238] text-[#F8FAFC] border-[#263750] hover:bg-[#1D2B43] hover:text-white'
              }`}
            >
              <Eye className="w-3.5 h-3.5 text-[#94A3B8]" />
              <span className="hidden sm:inline">{isPreviewMode ? 'Editöre Dön' : 'Önizleme'}</span>
            </button>

            {/* Autosave Status */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#111B2E] border border-[#263750] text-xs font-medium text-[#94A3B8]">
              {autosaveStatus === 'SYNCING' ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 text-[#2563EB] animate-spin" />
                  <span>Kaydediliyor...</span>
                </>
              ) : hasUnsavedChanges ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" />
                  <span className="text-[#F59E0B]">Kaydedilmemiş</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                  <span className="text-[#10B981]">Kaydedildi ✓</span>
                </>
              )}
            </div>

            {/* Prominent Primary Publish Action */}
            <button
              type="button"
              onClick={publishCurrentSite}
              disabled={isPublishing}
              className="px-4 py-2 rounded-xl bg-[#2563EB] hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-blue-600/30 transition active:scale-95 disabled:opacity-50 border border-blue-400/30"
              title="Değişiklikleri Canlı Web Sitesine Yayınla"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Yayınlanıyor...</span>
                </>
              ) : (
                <>
                  <Rocket className="w-3.5 h-3.5" />
                  <span>Canlı Yayınla</span>
                </>
              )}
            </button>

            {/* HTML / ZIP Export Trigger (Strict Super Admin Only) */}
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => setIsExportModalOpen(true)}
                title="HTML / CSS / JS ZIP Dışa Aktar"
                className="px-3 py-2 rounded-xl bg-[#162238] hover:bg-[#1D2B43] text-slate-200 hover:text-white border border-[#263750] text-xs font-bold flex items-center gap-1.5 transition active:scale-95 shadow-sm"
              >
                <Download className="w-3.5 h-3.5 text-blue-400" />
                <span className="hidden sm:inline">HTML İndir</span>
              </button>
            )}

            {/* Open Live Site (New Tab) */}
            <button
              type="button"
              onClick={onOpenLiveSite}
              title={
                activeSite?.customDomain
                  ? isSiteDomainConnected(activeSite)
                    ? `Canlı Önizleme & Siteyi Aç (${activeSite.customDomain})`
                    : `Canlı Önizleme & Siteyi Aç (Özel alan adı ${activeSite.customDomain} SSL kurulumunda - Adiosoft bağlantısı aktif)`
                  : 'Canlı Siteyi Yeni Sekmede Aç'
              }
              className={`p-2 rounded-xl border transition flex items-center gap-1.5 ${
                activeSite?.customDomain && !isSiteDomainConnected(activeSite)
                  ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30'
                  : 'bg-[#162238] hover:bg-[#1D2B43] text-[#94A3B8] hover:text-white border-[#263750]'
              }`}
            >
              <ExternalLink className="w-4 h-4" />
              {activeSite?.customDomain && !isSiteDomainConnected(activeSite) && (
                <span className="hidden xl:inline-flex items-center gap-1 text-[10px] text-amber-300 font-normal">
                  <Clock className="w-2.5 h-2.5 animate-pulse text-amber-400" />
                  SSL Kuruluyor
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ==================================================================== */}
        {/* 2. SECONDARY TOOLBAR (approx 56-64px) */}
        {/* ==================================================================== */}
        <div className="h-14 px-5 flex items-center justify-between bg-[#111B2E] border-b border-[#263750] text-xs font-semibold gap-3">
          {/* Left: Active Page Selector, Add Page, Undo/Redo */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Page Dropdown */}
            <div className="relative" ref={pageDropdownRef}>
              <button
                type="button"
                onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
                className="px-3 py-1.5 rounded-xl bg-[#162238] hover:bg-[#1D2B43] text-[#F8FAFC] border border-[#263750] flex items-center gap-2 text-xs font-bold transition shadow-sm"
              >
                <span className="text-[#94A3B8] font-normal text-[11px]">Sayfa:</span>
                <span className="truncate max-w-[130px] sm:max-w-[180px]">{activePage?.title || 'Ana Sayfa'}</span>
                <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8]" />
              </button>

              {isPageDropdownOpen && (
                <div className="absolute top-full mt-1.5 left-0 w-64 bg-[#162238] border border-[#263750] rounded-xl shadow-2xl p-1.5 z-50 animate-in fade-in">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] px-2.5 py-1.5">
                    Sayfalar ({(activeSite.pages || []).length})
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-0.5">
                    {(activeSite.pages || []).map((page) => (
                      <button
                        key={page.id}
                        type="button"
                        onClick={() => {
                          setActivePageId(page.id);
                          setIsPageDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition text-xs ${
                          page.id === activePageId
                            ? 'bg-[#2563EB] text-white font-bold'
                            : 'text-[#F8FAFC] hover:bg-[#1D2B43]'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span>{page.title}</span>
                          {page.isHomePage && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/60 border border-blue-400/30 text-blue-200">
                              Ana
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-[#94A3B8]">{page.slug}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add Page Button */}
            <button
              type="button"
              onClick={onOpenNewPageModal}
              title="Yeni Sayfa Ekle"
              className="p-2 rounded-xl bg-[#162238] hover:bg-[#1D2B43] text-[#F8FAFC] hover:text-white border border-[#263750] transition shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>

            {/* Undo / Redo Controls */}
            <div className="hidden sm:flex items-center bg-[#162238] p-0.5 rounded-xl border border-[#263750] ml-1">
              <button
                type="button"
                onClick={undo}
                disabled={!canUndo}
                title="Geri Al (Ctrl + Z)"
                className="p-1.5 rounded-lg text-[#F8FAFC] hover:bg-[#1D2B43] disabled:text-[#64748B] disabled:hover:bg-transparent transition"
              >
                <Undo2 className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={redo}
                disabled={!canRedo}
                title="İleri Al (Ctrl + Shift + Z)"
                className="p-1.5 rounded-lg text-[#F8FAFC] hover:bg-[#1D2B43] disabled:text-[#64748B] disabled:hover:bg-transparent transition"
              >
                <Redo2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Center: Device Mode Switcher (Desktop, Tablet, Mobile) & Width Dropdown */}
          <div className="flex items-center gap-2">
            {/* Device Switcher */}
            <div className="flex items-center bg-[#162238] p-1 rounded-xl border border-[#263750] shadow-sm">
              <button
                type="button"
                onClick={() => setDeviceMode('desktop')}
                title="Masaüstü Görünümü (1280px)"
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition text-xs ${
                  deviceMode === 'desktop'
                    ? 'bg-[#2563EB] text-white font-bold shadow'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1D2B43]'
                }`}
              >
                <Monitor className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Desktop</span>
              </button>
              <button
                type="button"
                onClick={() => setDeviceMode('tablet')}
                title="Tablet Görünümü (768px)"
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition text-xs ${
                  deviceMode === 'tablet'
                    ? 'bg-[#2563EB] text-white font-bold shadow'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1D2B43]'
                }`}
              >
                <Tablet className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Tablet</span>
              </button>
              <button
                type="button"
                onClick={() => setDeviceMode('mobile')}
                title="Mobil Görünüm (390px)"
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition text-xs ${
                  deviceMode === 'mobile'
                    ? 'bg-[#2563EB] text-white font-bold shadow'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1D2B43]'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Mobile</span>
              </button>
            </div>

            {/* Width Selector Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsViewportDropdownOpen(!isViewportDropdownOpen)}
                className="px-3 py-1.5 rounded-xl bg-[#162238] hover:bg-[#1D2B43] text-[#F8FAFC] border border-[#263750] flex items-center gap-1.5 text-xs font-mono transition shadow-sm"
              >
                <span>{currentPreset.label}</span>
                <ChevronDown className="w-3 h-3 text-[#94A3B8]" />
              </button>

              {isViewportDropdownOpen && (
                <div className="absolute top-full mt-1.5 right-0 w-52 bg-[#162238] border border-[#263750] rounded-xl shadow-2xl p-1.5 z-50 text-xs animate-in fade-in">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] px-2.5 py-1">
                    Genişlik Önayarları
                  </div>
                  <div className="space-y-0.5">
                    {PRESET_VIEWPORTS.map((preset) => (
                      <button
                        key={preset.width}
                        type="button"
                        onClick={() => {
                          setTestViewportWidth(preset.width);
                          setDeviceMode(preset.device);
                          setIsViewportDropdownOpen(false);
                          setIsCustomWidthActive(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 rounded-lg flex items-center justify-between transition ${
                          testViewportWidth === preset.width
                            ? 'bg-[#2563EB] text-white font-bold'
                            : 'text-[#F8FAFC] hover:bg-[#1D2B43]'
                        }`}
                      >
                        <span>{preset.label}</span>
                        <span className="text-[10px] text-[#94A3B8]">{preset.width}px</span>
                      </button>
                    ))}
                  </div>

                  {/* Custom Width Input */}
                  <div className="border-t border-[#263750] mt-1.5 pt-1.5 px-1">
                    {!isCustomWidthActive ? (
                      <button
                        type="button"
                        onClick={() => setIsCustomWidthActive(true)}
                        className="w-full text-left px-2 py-1 text-xs text-[#94A3B8] hover:text-white rounded hover:bg-[#1D2B43] flex items-center justify-between"
                      >
                        <span>Özel Genişlik...</span>
                        <span className="text-[10px]">px</span>
                      </button>
                    ) : (
                      <form onSubmit={handleCustomWidthSubmit} className="flex items-center gap-1.5 py-1">
                        <input
                          type="number"
                          min={320}
                          max={2560}
                          placeholder="Genişlik (px)"
                          value={customWidthInput}
                          onChange={(e) => setCustomWidthInput(e.target.value)}
                          className="w-full px-2 py-1 bg-[#111B2E] border border-[#263750] rounded text-xs text-white focus:outline-none focus:border-[#2563EB]"
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="px-2 py-1 bg-[#2563EB] text-white rounded text-xs font-bold hover:bg-blue-500"
                        >
                          Uygula
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Add Section Button in Toolbar */}
            {onOpenAddSectionModal && (
              <button
                type="button"
                onClick={onOpenAddSectionModal}
                className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#2563EB]/15 hover:bg-[#2563EB]/25 text-blue-400 hover:text-blue-300 border border-blue-500/30 text-xs font-bold transition shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Bölüm Ekle</span>
              </button>
            )}
          </div>

          {/* Right: Section count & Properties panel toggle */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-[#162238] border border-[#263750] text-[11px] font-medium text-[#94A3B8]">
              <Layers className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>{activePage?.sections?.length || 0} Bölüm</span>
            </div>

            {onToggleProperties && (
              <button
                type="button"
                onClick={onToggleProperties}
                title={isPropertiesOpen ? 'Özellik Panelini Gizle' : 'Özellik Panelini Aç'}
                className={`p-2 rounded-xl border text-xs font-semibold transition ${
                  isPropertiesOpen
                    ? 'bg-[#2563EB] text-white border-blue-400/30'
                    : 'bg-[#162238] text-[#94A3B8] hover:text-white border-[#263750] hover:bg-[#1D2B43]'
                }`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Responsive QA Modal */}
      {isQAModalOpen && <ResponsiveQAModal onClose={() => setIsQAModalOpen(false)} />}

      {/* HTML / CSS / JS Export Modal (Strict Super Admin Only) */}
      {isSuperAdmin && isExportModalOpen && activeSite && (
        <WebsiteExportModal
          site={activeSite}
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
        />
      )}
    </>
  );
};
